// Shared types + helpers for the Mastery Approvals supervisor workspace.

export type ApprovalStatus = "pending" | "approved" | "rejected";
export type StatusFilter = ApprovalStatus | "all";

export type Criteria = {
  family: "percent_correct" | "frequency";
  direction: "increase" | "reduce";
  threshold: number | null;
  unit: string;
  sessions: number | null;
  min_trials_per_session: number | null;
};

export type ApprovalRow = {
  id: number;
  targetId: number;
  programId: number | null;
  workflowId: number | null;
  phaseId: number;
  phaseName: string;
  requestedOutcome: "status" | "transition";
  requestedNextStatus: string;
  status: ApprovalStatus;
  targetName: string | null;
  targetStatus: string | null;
  programName: string | null;
  studentFirstName: string | null;
  studentLastName: string | null;
  studentId: number | null;
  studentGrade: string | null;
  workflowName: string | null;
  workflowObjective: string | null;
  workflowObjectiveKey: string | null;
  requestedByName: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  triggeredSessionId: number | null;
  created_at: string;
  criteria: Criteria;
};

// Palette — muted clinical semantics, no dark saturated greens.
export const PALETTE = {
  navy: "#1b3a6b",
  navy2: "#31577f",
  ink: "#0f172a",
  slate: "#475569",
  border: "#e3ebf2",
  surface: "#ffffff",
  pageBg: "#f7f9fc",
  pending: { bg: "#fdf3d4", fg: "#8a6604", accent: "#c99b12" },
  approved: { bg: "#e6f4ed", fg: "#166534", accent: "#4a9067" },
  rejected: { bg: "#fdecec", fg: "#9b1c1c", accent: "#c56767" },
  info: { bg: "#eef2ff", fg: "#3730a3", accent: "#4f6ef7" },
} as const;

export const statusTint = (s: ApprovalStatus) =>
  s === "approved" ? PALETTE.approved : s === "rejected" ? PALETTE.rejected : PALETTE.pending;

// Render Percent vs Frequency wording purely from the criteria DTO.
// Never invents defaults; if a field is missing, degrade gracefully.
export const criteriaHeadline = (c: Criteria): string => {
  const t = c.threshold;
  const sessions = c.sessions;
  const sessionPhrase = sessions ? ` across ${sessions} session${sessions === 1 ? "" : "s"}` : "";
  if (c.family === "frequency") {
    const value = t !== null ? `${t}` : "—";
    const label = c.direction === "reduce" ? "Maximum Frequency" : "Minimum Frequency";
    const unit = c.unit && c.unit !== "count" ? ` ${c.unit}` : " occurrences";
    return `${label}: ${value}${unit}${sessionPhrase}`;
  }
  // percent_correct
  const value = t !== null ? `${t}%` : "—";
  const label = c.direction === "reduce" ? "Maximum Percent Correct" : "Percent Correct";
  return `${label}: ${value}${sessionPhrase}`;
};

export const objectiveLabel = (row: ApprovalRow): string => {
  if (row.workflowObjective) return row.workflowObjective;
  const key = (row.workflowObjectiveKey || "").replace(/_/g, " ").trim();
  if (!key) return row.criteria.family === "frequency"
    ? (row.criteria.direction === "reduce" ? "Reduce Frequency" : "Increase Frequency")
    : "Percent Correct";
  return key.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

export const destinationLabel = (row: ApprovalRow): string =>
  row.requestedOutcome === "status"
    ? `Set target status → ${row.requestedNextStatus}`
    : `Advance to phase → ${row.requestedNextStatus}`;

export const studentName = (row: ApprovalRow): string =>
  [row.studentFirstName, row.studentLastName].filter(Boolean).join(" ").trim() || "—";

export const initials = (name: string): string =>
  name.trim().split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?";
